import { useState, type FormEvent } from 'react'
import { mensagemDoErro } from '../../api/erros'
import { useEnviarDocumento } from '../../api/queries/documentos'
import { Botao } from '../../components/Botao'
import { CampoDeSelecao, CampoDeTexto } from '../../components/campos'
import { Dialogo } from '../../components/Dialogo'
import { TEXTOS } from '../../lib/glossario'
import type { DocumentoDoContrato, TipoDeDocumento } from '../../types/documentos'
import { nomeSugerido, tamanhoLegivel } from './formato'

/** Os tipos na ordem em que a fiscalização os envia: o TR e o contrato primeiro. */
const TIPOS: { valor: TipoDeDocumento; rotulo: string }[] = [
  { valor: 'TERMO_DE_REFERENCIA', rotulo: 'Termo de Referência' },
  { valor: 'CONTRATO', rotulo: 'Contrato' },
  { valor: 'ADITIVO', rotulo: 'Termo aditivo' },
  { valor: 'ANEXO_DO_CONTRATO', rotulo: 'Anexo do contrato' },
  { valor: 'TERMO_DE_RECEBIMENTO', rotulo: 'Termo de recebimento' },
  { valor: 'ATA', rotulo: 'Ata' },
  { valor: 'OFICIO', rotulo: 'Ofício' },
  { valor: 'OUTRO', rotulo: 'Outro documento' },
]

const ACEITOS = '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain'

/**
 * Enviar um documento: o arquivo, o nome que ele terá nas citações e que documento é.
 *
 * O nome vem sugerido a partir do arquivo — obrigatório e quase sempre aceito como está. O tipo
 * começa em "Termo de Referência" quando ainda não há nenhum: é o primeiro que a consulta precisa.
 *
 * @param props se já existe um TR, e o que fazer ao terminar
 * @returns o diálogo
 */
export function DialogoDeEnvio({
  jaTemTr,
  aoFechar,
  aoEnviar,
}: {
  jaTemTr: boolean
  aoFechar: () => void
  aoEnviar: (documento: DocumentoDoContrato) => void
}) {
  const t = TEXTOS.documentos.envio
  const envio = useEnviarDocumento()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [titulo, setTitulo] = useState('')
  const [tipo, setTipo] = useState<TipoDeDocumento>(jaTemTr ? 'CONTRATO' : 'TERMO_DE_REFERENCIA')
  const [tentou, setTentou] = useState(false)

  const erroDoArquivo = tentou && !arquivo ? 'Escolha o arquivo do documento.' : undefined
  const erroDoTitulo = tentou && titulo.trim().length < 3 ? 'Dê um nome com pelo menos 3 letras.' : undefined

  function escolher(lista: FileList | null) {
    const escolhido = lista?.[0] ?? null
    setArquivo(escolhido)
    if (escolhido && !titulo.trim()) {
      setTitulo(nomeSugerido(escolhido.name))
    }
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    setTentou(true)
    if (!arquivo || titulo.trim().length < 3) {
      return
    }
    envio.mutate({ arquivo, titulo: titulo.trim(), tipo }, { onSuccess: aoEnviar })
  }

  return (
    <Dialogo
      titulo={t.titulo}
      descricao={t.descricao}
      aoFechar={envio.isPending ? () => undefined : aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar} disabled={envio.isPending}>
            Cancelar
          </Botao>
          <Botao aparencia="primario" type="submit" form="envio-de-documento" disabled={envio.isPending}>
            {envio.isPending ? t.enviando : t.confirmar}
          </Botao>
        </>
      }
    >
      <form id="envio-de-documento" onSubmit={enviar} className="space-y-4" noValidate>
        <CampoDeTexto
          rotulo={t.arquivo}
          type="file"
          accept={ACEITOS}
          erro={erroDoArquivo}
          ajuda={arquivo ? `${arquivo.name} · ${tamanhoLegivel(arquivo.size)}` : undefined}
          onChange={(evento) => escolher(evento.currentTarget.files)}
          disabled={envio.isPending}
          className="file:mr-3 file:rounded-md file:border-0 file:bg-niteroi-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-niteroi-700"
        />
        <CampoDeTexto
          rotulo={t.nome}
          ajuda={t.nomeAjuda}
          value={titulo}
          maxLength={200}
          erro={erroDoTitulo}
          onChange={(evento) => setTitulo(evento.currentTarget.value)}
          disabled={envio.isPending}
        />
        <CampoDeSelecao
          rotulo={t.tipo}
          value={tipo}
          onChange={(evento) => setTipo(evento.currentTarget.value as TipoDeDocumento)}
          disabled={envio.isPending}
        >
          {TIPOS.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </CampoDeSelecao>
        {envio.isError && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
            {mensagemDoErro(envio.error)}
          </p>
        )}
      </form>
    </Dialogo>
  )
}
